import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Calendar as CalendarIcon, User, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/lib/api";

/**
 * Vista Planificador (kanban tipo taller).
 *
 * Columnas = técnicos. Tarjetas = reparaciones del día seleccionado (programadas).
 * Al arrastrar una tarjeta a otra columna se reasigna el técnico y se sincroniza
 * automáticamente con Google Calendar (via backend).
 */
export default function Planner() {
  const today = new Date().toISOString().substring(0, 10);
  const [date, setDate] = useState(today);
  const [technicians, setTechnicians] = useState([]);
  const [repairs, setRepairs] = useState([]); // todas las del día
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [usersR, repsR] = await Promise.all([
        api.get("/users"),
        api.get("/repairs", { params: { limit: 500 } }),
      ]);
      const techs = (usersR.data || []).filter(
        (u) => (u.roles || []).includes("technician") && u.active !== false
      );
      setTechnicians(techs);
      // Filtrar reparaciones del día (por scheduled_at)
      const dayStart = new Date(date + "T00:00:00").getTime();
      const dayEnd = new Date(date + "T23:59:59").getTime();
      const list = (repsR.data || []).filter((r) => {
        if (!r.scheduled_at) return false;
        const t = new Date(r.scheduled_at).getTime();
        return t >= dayStart && t <= dayEnd;
      });
      setRepairs(list);
    } catch (e) {
      toast.error("No se pudo cargar el planificador");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  // Columnas: técnicos + "Sin asignar"
  const columns = [
    { id: "unassigned", name: "Sin asignar", color: "#525252" },
    ...technicians.map((t) => ({ id: t.id, name: t.name, color: t.google_calendar_color || "#6E8B3D" })),
  ];

  const repairsByCol = (colId) =>
    repairs.filter((r) => (colId === "unassigned" ? !r.assigned_to : r.assigned_to === colId))
      .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""));

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { draggableId, source, destination } = result;
    if (source.droppableId === destination.droppableId) return;
    const repair = repairs.find((r) => r.id === draggableId);
    if (!repair) return;
    const newTechId = destination.droppableId === "unassigned" ? null : destination.droppableId;
    // Optimistic update
    setRepairs((prev) => prev.map((r) => r.id === draggableId ? { ...r, assigned_to: newTechId } : r));
    setSavingId(draggableId);
    try {
      await api.put(`/repairs/${draggableId}`, { assigned_to: newTechId });
      toast.success(`Reparación reasignada${newTechId ? "" : " (sin técnico)"}`);
    } catch (e) {
      toast.error("No se pudo reasignar");
      load(); // revert
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#6E8B3D]">
            // Asignación rápida
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-black mt-1">Planificador</h1>
          <p className="text-xs text-[#525252] mt-1">
            Arrastra reparaciones entre técnicos. Se sincroniza automáticamente con Google Calendar.
          </p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] block mb-1">Día</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="planner-date"
              className="ds-card px-3 py-2 text-sm"
            />
          </div>
          <button
            onClick={load}
            data-testid="planner-refresh"
            className="border border-[#E5E5E0] px-3 py-2 text-xs font-mono uppercase hover:bg-[#F7F7F4] inline-flex items-center gap-1"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Recargar
          </button>
        </div>
      </div>

      {technicians.length === 0 && (
        <div className="ds-card p-6 text-sm text-[#525252] flex items-center gap-2">
          <AlertCircle size={16} /> No hay técnicos creados. Ve a Usuarios y crea al menos uno.
        </div>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {columns.map((col) => {
            const cards = repairsByCol(col.id);
            return (
              <Droppable key={col.id} droppableId={col.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`bg-white border ${snapshot.isDraggingOver ? "border-[#6E8B3D] bg-[#F7F7F4]" : "border-[#E5E5E0]"} min-h-[400px]`}
                    data-testid={`planner-col-${col.id}`}
                  >
                    <div
                      className="px-3 py-2 border-b border-[#E5E5E0] flex items-center justify-between"
                      style={{ borderTop: `3px solid ${col.color}` }}
                    >
                      <div className="flex items-center gap-2">
                        <User size={13} className="text-[#525252]" />
                        <span className="font-medium text-sm">{col.name}</span>
                      </div>
                      <span className="font-mono text-[10px] text-[#525252]">{cards.length}</span>
                    </div>
                    <div className="p-2 space-y-2">
                      {cards.length === 0 && (
                        <div className="text-[11px] text-[#525252] italic p-2 text-center">
                          (vacío)
                        </div>
                      )}
                      {cards.map((r, i) => (
                        <Draggable key={r.id} draggableId={r.id} index={i}>
                          {(prov, snap) => (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              {...prov.dragHandleProps}
                              className={`bg-[#FAFAF8] border border-[#E5E5E0] p-2.5 cursor-grab ${snap.isDragging ? "shadow-lg" : ""} ${savingId === r.id ? "opacity-50" : ""}`}
                              data-testid={`planner-card-${r.id}`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <Link
                                  to={`/repairs/${r.id}`}
                                  className="font-mono text-[10px] text-[#6E8B3D] hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {r.code}
                                </Link>
                                <span className="text-[10px] font-mono text-[#525252]">
                                  {new Date(r.scheduled_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
                                </span>
                              </div>
                              <div className="text-sm font-medium mt-1 truncate">
                                {r.device_type} {r.model}
                              </div>
                              <div className="text-[11px] text-[#525252] truncate">
                                {r.customer_name || "—"}
                              </div>
                              {r.issue && (
                                <div className="text-[11px] text-[#525252] truncate italic mt-0.5" title={r.issue}>
                                  {r.issue}
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
}
